/**
 * Justesims All Destinations page — accessible tabs (Popular / Country /
 * Region / All) plus a live search filter.
 *
 * Search reuses the "All" tab's grid as its filter target, since that tab
 * already holds every destination (the other three tabs are curated/grouped
 * subsets of the same data). Typing hides the tabs and shows a filtered view
 * of the All grid; clearing the box restores whichever tab was active before.
 */
(function () {
  function initTabs(root) {
    const tabsWrap = root.querySelector('[data-je-ad-tabs]');
    const tabs = Array.from(root.querySelectorAll('[role="tab"]'));
    const panels = Array.from(root.querySelectorAll('[role="tabpanel"]'));
    if (!tabs.length) return null;

    function showPanelFor(tab) {
      const targetId = tab.getAttribute('aria-controls');
      panels.forEach((p) => {
        p.hidden = p.id !== targetId;
      });
    }

    function activate(tab, setFocus) {
      tabs.forEach((t) => {
        const selected = t === tab;
        t.setAttribute('aria-selected', selected ? 'true' : 'false');
        t.tabIndex = selected ? 0 : -1;
      });
      showPanelFor(tab);
      if (setFocus) tab.focus();
    }

    tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => activate(tab, false));
      tab.addEventListener('keydown', (e) => {
        let idx = null;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') idx = (i + 1) % tabs.length;
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') idx = (i - 1 + tabs.length) % tabs.length;
        else if (e.key === 'Home') idx = 0;
        else if (e.key === 'End') idx = tabs.length - 1;
        if (idx !== null) {
          e.preventDefault();
          activate(tabs[idx], true);
        }
      });
    });

    return {
      // Search box cleared — bring back the tab bar and whichever panel was selected.
      restore() {
        if (tabsWrap) tabsWrap.hidden = false;
        const active = tabs.find((t) => t.getAttribute('aria-selected') === 'true') || tabs[0];
        showPanelFor(active);
      },
      // User is typing — hide the tab bar, force the All panel (the search target) open.
      showSearchMode() {
        if (tabsWrap) tabsWrap.hidden = true;
        panels.forEach((p) => {
          p.hidden = p.id !== 'je-ad-panel-all';
        });
      },
    };
  }

  function initSearch(root, tabsController) {
    const input = root.querySelector('[data-je-ad-search-input]');
    const target = root.querySelector('[data-je-ad-search-target]');
    const emptyMsg = root.querySelector('[data-je-ad-search-empty]');
    const form = root.querySelector('[data-je-ad-search-form]');
    if (!input || !target) return;

    const items = Array.from(target.querySelectorAll('li[data-je-ad-name]'));

    function filter(term) {
      const q = term.trim().toLowerCase();

      if (!q) {
        items.forEach((li) => {
          li.hidden = false;
        });
        if (emptyMsg) emptyMsg.hidden = true;
        if (tabsController) tabsController.restore();
        return;
      }

      if (tabsController) tabsController.showSearchMode();
      let visibleCount = 0;
      items.forEach((li) => {
        const match = li.getAttribute('data-je-ad-name').toLowerCase().includes(q);
        li.hidden = !match;
        if (match) visibleCount += 1;
      });
      if (emptyMsg) emptyMsg.hidden = visibleCount !== 0;
    }

    input.addEventListener('input', () => filter(input.value));
    if (form) {
      form.addEventListener('submit', (e) => {
        // Filtering is live — don't navigate to Dawn's /search results.
        e.preventDefault();
        filter(input.value);
      });
    }

    // Support landing here with ?q= from the hero/footer search boxes. This has
    // to be read from the URL in JS, not Liquid's `search.terms` — that object
    // is only populated on Shopify's native /search route, and is always blank
    // on a regular page template like this one even when ?q= is in the URL.
    const params = new URLSearchParams(window.location.search);
    const initialQuery = params.get('q');
    if (initialQuery) {
      input.value = initialQuery;
      filter(initialQuery);
    }
  }

  function init(scope) {
    (scope || document).querySelectorAll('.je-ad').forEach((root) => {
      const tabsController = initTabs(root);
      initSearch(root, tabsController);
    });
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', () => init());

  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
